// src/ClientForm.ts

import * as Yup from 'yup';
import { getValidationSchema } from '../helpers/FormSchema';
import { FormType } from '../helpers/FormType';

type FormElement = HTMLFormElement;
type ButtonElement = HTMLButtonElement;

const ClientForm = () => {
    const handleButtonClick = async (
        formElement: FormElement,
        buttonElement: ButtonElement,
    ): Promise<void> => {
        const formData = new FormData(formElement);

        // Get form type (assuming it's set as a data attribute on the form element)
        const formType = formElement.getAttribute(
            'data-formtype',
        ) as FormType | null;

        // Ensure the formType exists and is valid
        if (!formType) {
            throw new Error('Form type is not specified or invalid');
        }


        // Get the validation schema for the form type
        const validationSchema = getValidationSchema(formType);

        // Convert FormData to a plain object for validation
        const formValues: Record<string, any> = {};
        formData.forEach((value, key) => {
            formValues[key] = value;
        });

        try {
            // Validate form data using Yup
            await validationSchema.validate(formValues, { abortEarly: false });

            // If validation passes, proceed with the fetch request
            buttonElement.classList.add('spinner-btn--loading');
            buttonElement.disabled = true;

            const url = formElement.action;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: formData, // Send form data as is (FormData object)
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const htmlContent = await response.text();

            const targetElementId = formElement.getAttribute('data-html');
            if (targetElementId) {
                const targetElement = document.getElementById(targetElementId);
                if (targetElement) {
                    targetElement.innerHTML = htmlContent;
                } else {
                    console.error(`Element with ID '${targetElementId}' not found`);
                }
            }
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                // Handle Yup validation errors (e.g., show validation messages)
                console.error('Validation errors:', error.errors);
                handleValidationErrors(formType, error);

                // Optionally display error messages on the form (implementation depends on your UI)
            } else {
                console.error('Fetch error:', error);
                // Handle fetch errors (e.g., show a global error message)
            }
        } finally {
            buttonElement.classList.remove('spinner-btn--loading');
            buttonElement.disabled = false;
        }
    };

    const handleSubmit = (event: Event): void => {
        event.preventDefault();

        const formElement = event.currentTarget as FormElement;
        const submitButton = formElement.querySelector(
            'button.js-spinner-btn',
        ) as ButtonElement;

        if (submitButton) {
            handleButtonClick(formElement, submitButton);
        }
    };

    const handleValidationErrors = (
        formType: FormType,
        errors: Yup.ValidationError,
    ) => {
        errors.inner.forEach(error => {
            const inputName = error.path?.toLowerCase().replace('.', '-'); // Get the field name
            const formGroup = document.getElementById(
                `${formType}-${inputName}-group`,
            ); // Assume IDs are set as {fieldName}Group
            console.log(
                'error',
                error,
                'formgroup',
                `${formType}-${inputName}-group`,
                formGroup,
            );
            if (formGroup) {
                formGroup.classList.add('has-error'); // Add error class

                const errorMessage = formGroup.querySelector('.error-message');
                if (errorMessage) {
                    errorMessage.textContent = error.message; // Show error message
                }
            }
        });
    };

    return { handleSubmit };
};

export default ClientForm;
